import type { JSX } from 'react';

interface ScriptSection {
  title: string;
  timing?: string;
  body: string;
  kind: 'hook' | 'block' | 'closing' | 'plain';
}

/**
 * Generated scripts are stored as portable Markdown, but rehearsed as speech.
 * Parsing happens only for presentation: copying and pinning keep using the
 * original string returned by the service.
 */
export function parseScript(text: string): ScriptSection[] {
  const sections: ScriptSection[] = [];
  let current: { heading: string; lines: string[] } | null = null;

  function commit(): void {
    if (!current) return;
    const title = current.heading.trim();
    const [label = title, timing] = title.split(/\s*·\s*/, 2);
    sections.push({
      title: displayTitle(label),
      ...(timing ? { timing } : {}),
      body: current.lines.join('\n').trim(),
      kind: sectionKind(label)
    });
  }

  for (const line of text.split('\n')) {
    const heading = /^##\s+(.+)$/.exec(line);
    if (heading) {
      commit();
      current = { heading: heading[1] ?? '', lines: [] };
      continue;
    }
    if (!current) current = { heading: 'ROTEIRO', lines: [] };
    current.lines.push(line);
  }
  commit();
  return sections.filter((section) => section.title !== '' || section.body !== '');
}

export function ScriptBody({ text }: { text: string }): JSX.Element {
  const sections = parseScript(text);

  return (
    <article className="rvk-script-body" aria-label="Roteiro gerado">
      {sections.map((section, index) => (
        <section
          key={`${section.title}-${index}`}
          className="rvk-script-section"
          data-kind={section.kind}
        >
          <header className="rvk-script-section-header">
            <span className="rvk-script-step" aria-hidden>
              {String(index + 1).padStart(2, '0')}
            </span>
            <div className="rvk-script-section-label">
              <h2>{section.title}</h2>
              {section.timing && <span>{section.timing}</span>}
            </div>
          </header>
          <div className="rvk-script-speech">
            {section.body.split(/\n{2,}/).map((paragraph, paragraphIndex) => (
              <p key={paragraphIndex}>{paragraph}</p>
            ))}
          </div>
        </section>
      ))}
    </article>
  );
}

function displayTitle(value: string): string {
  return value
    .trim()
    .toLocaleLowerCase('pt-BR')
    .replace(/(^|\s)\p{L}/gu, (letter) => letter.toLocaleUpperCase('pt-BR'));
}

function sectionKind(value: string): ScriptSection['kind'] {
  const folded = value.trim().toLocaleLowerCase('pt-BR');
  if (folded === 'gancho') return 'hook';
  if (folded === 'fechamento') return 'closing';
  if (folded.startsWith('bloco')) return 'block';
  return 'plain';
}
